import React, { useEffect, useRef } from 'react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import Spinner from '../ui/Spinner';

function formatDateSeparator(date) {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMMM d, yyyy');
}

function DateSeparator({ date }) {
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">
        {formatDateSeparator(date)}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

function ChatBubble({ message }) {
  const isOutbound = message.direction === 'outbound';
  const ts = new Date(message.created_at);

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[75%] ${isOutbound ? 'items-end' : 'items-start'} flex flex-col`}>
        {isOutbound && message.agent_name && (
          <span className="text-[11px] text-gray-400 mb-0.5 px-1">{message.agent_name}</span>
        )}
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words ${
            isOutbound
              ? 'bg-[#00AAFF] text-white rounded-br-md'
              : 'bg-[#f1f5f9] text-gray-900 rounded-bl-md'
          }`}
        >
          {message.body}
        </div>
        <span className={`text-[10px] text-gray-400 mt-1 px-1 ${isOutbound ? 'text-right' : 'text-left'}`}>
          {format(ts, 'MMM d, h:mm a')}
        </span>
      </div>
    </div>
  );
}

export default function SMSThread({ messages, loading, emptyAction }) {
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (messages.length > 0 && messages.length !== prevLengthRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: prevLengthRef.current === 0 ? 'auto' : 'smooth' });
    }
    prevLengthRef.current = messages.length;
  }, [messages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading messages…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700">No messages yet</p>
        <p className="text-xs text-gray-400 mt-1">Start the conversation by sending an SMS.</p>
        {emptyAction}
      </div>
    );
  }

  let lastDate = null;
  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-3">
      {messages.map((msg, idx) => {
        const msgDate = new Date(msg.created_at);
        let showSeparator = false;
        if (!lastDate || !isSameDay(lastDate, msgDate)) {
          showSeparator = true;
        }
        lastDate = msgDate;

        return (
          <React.Fragment key={msg.id || idx}>
            {showSeparator && <DateSeparator date={msgDate} />}
            <ChatBubble message={msg} />
          </React.Fragment>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
